import { useState, useContext, useCallback } from 'react';
import type {StagedFile} from '@securspace/securspace-ui-kit';
import { checkin } from '../../../components/checkin/request/check-in-requests';
import { uploadInventoryImageBlob, updateGallery } from '../../../components/checkin/request/file-upload-requests';
import { getErrorMessageForStandardResponse } from '../../../util/NetworkErrorUtil';
import { AppContext } from '../../../context/app-context';
import { useFaro } from '../../../context/faro-context';
import { CheckInSubmitData, UseCheckInSubmitReturn, CheckInPayload, CheckInResult } from './interfaces';
import { verbiage } from './verbiage';

interface PreparedGalleryImage {
  blob: Blob;
  originalName: string;
  contentType: string;
}

const IMAGE_EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

/**
 * Hook encapsulating the full check-in submission flow:
 * 1. POST check-in data
 * 2. Upload images (if any)
 * 3. Update gallery table
 *
 * Returns a {@link CheckInResult} describing the outcome of each phase.
 * The caller is responsible for displaying messages based on the result.
 * Gallery upload errors are also reported to Faro for production monitoring.
 *
 * @returns submit function and loading state.
 */
export const useCheckInSubmit = (): UseCheckInSubmitReturn => {
  const [loading, setLoading] = useState(false);
  const appContext = useContext(AppContext);
  const faro = useFaro();

  /**
   * Submits check-in data and handles image uploads.
   * @param data CheckInSubmitData containing all necessary info for check-in and images
   * @returns CheckInResult describing what succeeded or failed
   */
  const submit = useCallback(async (data: CheckInSubmitData): Promise<CheckInResult> => {
    const { bookingId, locationId, buyerId, correlationId, equipmentDataErrorMessage, formValues } = data;
    const {stagedFiles} = formValues;

    setLoading(true);

    try {
      let preparedGalleryImages: PreparedGalleryImage[];
      try {
        // Snapshot transient preview URLs before the check-in request. The form may
        // revoke those URLs if it is reset or unmounted while the request is pending.
        preparedGalleryImages = await prepareGalleryImages(stagedFiles);
      } catch {
        return {
          checkInSucceeded: false,
          gallerySucceeded: null,
          errorMessage: verbiage.imagePreparationFailed,
        };
      }

      const payload: CheckInPayload = {
        bookingId,
        locationId,
        buyerId,
        containerNumber: formValues.containerNumber,
        trailerNumber: formValues.trailerNumber,
        chassisNumber: formValues.chassisNumber,
        chassisLicensePlateNumber: formValues.chassisLicensePlateNumber,
        sealNumber: formValues.sealNumber,
        driverFirstName: formValues.driverFirstName,
        driverLastName: formValues.driverLastName,
        driverLicenseNumber: formValues.driverLicenseNumber,
        truckLicensePlateNumber: formValues.truckLicensePlateNumber,
        notes: formValues.notes,
        assetSize: formValues.assetSize,
        assetType: formValues.assetType,
        equipmentDataErrorMessage,
        correlationId,
        checkInUser: appContext.user?.loggedInUsername,
        scacCode: formValues.scacCode,
      };

      const resp = await checkin(payload);
      const responseData = resp.body;

      // -------- gallery upload flow --------
      if (responseData?.id) {
        if (preparedGalleryImages.length === 0) {
          return { checkInSucceeded: true, gallerySucceeded: null, errorMessage: null };
        }

        try {
          await saveGalleryImages(responseData.id, preparedGalleryImages);
          return { checkInSucceeded: true, gallerySucceeded: true, errorMessage: null };
        } catch (galleryError) {
          const error = galleryError instanceof Error ? galleryError : new Error(String(galleryError));
          faro.pushError(error, {
            context: 'gallery-upload',
            inventoryId: String(responseData.id),
          });
          return {
            checkInSucceeded: true,
            gallerySucceeded: false,
            errorMessage: verbiage.galleryUploadFailed,
          };
        }
      }

      return {
        checkInSucceeded: false,
        gallerySucceeded: null,
        errorMessage: verbiage.checkInFailedNoId,
      };
    } catch (err: unknown) {
      return {
        checkInSucceeded: false,
        gallerySucceeded: null,
        errorMessage: getErrorMessageForStandardResponse(err, verbiage.checkInFailedDefault),
      };
    } finally {
      setLoading(false);
    }
  }, [appContext.user?.loggedInUsername, faro]);

  return { submit, loading };
};

/**
 * Uploads gallery images for a checked-in inventory item.
 * Throws on error to allow caller to handle appropriately.
 *
 * @param inventoryId ID of the inventory item just checked in
 * @param preparedImages Durable image Blobs prepared before the check-in request
 * @returns Promise that resolves when uploads and gallery update are complete
 */
async function saveGalleryImages(
  inventoryId: string,
  preparedImages: PreparedGalleryImage[]
): Promise<void> {
  const galleryFileNames: string[] = [];

  for (const preparedImage of preparedImages) {
    galleryFileNames.push(await uploadSingleFile(preparedImage, inventoryId));
  }

  await updateGallery(inventoryId, galleryFileNames);
}

/**
 * Resolves each caller-owned preview URL into a durable Blob before check-in.
 */
async function prepareGalleryImages(stagedFiles: StagedFile[]): Promise<PreparedGalleryImage[]> {
  return Promise.all(stagedFiles.map(async (stagedFile) => {
    const response = await fetch(stagedFile.previewUrl);
    if (!response.ok) {
      throw new Error(`Unable to read image ${stagedFile.file.name}`);
    }

    const blob = await response.blob();
    return {
      blob,
      originalName: stagedFile.file.name,
      contentType: blob.type || stagedFile.file.type,
    };
  }));
}

/**
 * Uploads a prepared image and returns its persisted filename.
 * Throws on error.
 */
async function uploadSingleFile(
  preparedImage: PreparedGalleryImage,
  inventoryId: string
): Promise<string> {
  const {blob, originalName, contentType} = preparedImage;
  const fileName = `${new Date().getTime()}_${withContentTypeExtension(originalName, contentType)}`;
  return uploadInventoryImageBlob(
    inventoryId,
    blob,
    fileName,
    contentType
  );
}

const withContentTypeExtension = (fileName: string, contentType: string): string => {
  const extension = IMAGE_EXTENSION_BY_MIME_TYPE[contentType];
  if (!extension) return fileName;

  const lastDotIndex = fileName.lastIndexOf('.');
  const baseName = lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName;
  return `${baseName}.${extension}`;
};

